<template>
  <div style="padding: 20px" class="sim">
    <header>
      <h2>{{ $t('SIM.title') }}</h2>
      <a-space>
        <a-button type="primary" @click="getSIMList()">{{ $t('buttons.search') }}</a-button>
        <a-button type="primary" @click="handleExport" :loading="isExporting">{{ $t('buttons.export') }}</a-button>
      </a-space>
    </header>
    <el-table v-loading="isTableLoading" :data="SIMs">
      <el-table-column width="170">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.giccid') }}
            <span class="icons unset" style="cursor: pointer;" data-index="giccid" @click="handleSortConditionChange($event, 'giccid')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span>
          </div>
          <a-input allow-clear @keyup.enter="getSIMList" v-model.trim="queryParams.giccid" />
        </template>
        <template slot-scope="scope">
          <text-highlight :queries="highlightInfo.giccid">{{ scope.row.giccid }}</text-highlight>
        </template>
      </el-table-column>
      <el-table-column width="120">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.status') }}</div>
          <a-select style="width: 100%;" @change="getSIMList" v-model="filterParams.status" dropdownClassName="tableWarningTagOption">
            <a-select-option value="">{{ $t('common.all') }}</a-select-option>
            <a-select-option value="null">{{ "  " }}</a-select-option>
            <a-select-option v-for="(v, k) in simTypeMap" :key="k">{{ v }}</a-select-option>
          </a-select>
        </template>
        <template slot-scope="scope">
          {{ simTypeMap[scope.row.status] }}
        </template>
      </el-table-column>
      <el-table-column width="130">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.deviceCode') }}<span class="icons unset" style="cursor: pointer;" data-index="deviceCode" @click="handleSortConditionChange($event, 'deviceCode')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-input allow-clear @keyup.enter="getSIMList" v-model.trim="queryParams.deviceCode" />
        </template>
        <template slot-scope="scope">
          <text-highlight :queries="highlightInfo.deviceCode">{{ scope.row.deviceCode }}</text-highlight>
        </template>
      </el-table-column>
      <el-table-column width="120">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.deviceName') }}<span class="icons unset" style="cursor: pointer;" data-index="deviceName" @click="handleSortConditionChange($event, 'deviceName')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-input allow-clear @keyup.enter="getSIMList" v-model.trim="queryParams.deviceName" />
        </template>
        <template slot-scope="scope">
          <text-highlight :queries="highlightInfo.deviceName">{{ scope.row.deviceName }}</text-highlight>
        </template>
      </el-table-column>
      <el-table-column width="100">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.model') }}<span class="icons unset" style="cursor: pointer;" data-index="model" @click="handleSortConditionChange($event, 'model')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-input allow-clear @keyup.enter="getSIMList" v-model.trim="queryParams.model" />
        </template>
        <template slot-scope="scope">
          <text-highlight :queries="highlightInfo.model">{{ scope.row.model }}</text-highlight>
        </template>
      </el-table-column>
      <el-table-column width="120">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('common.dtuCode') }}<span class="icons unset" style="cursor: pointer;" data-index="dtuCode" @click="handleSortConditionChange($event, 'dtuCode')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-input allow-clear @keyup.enter="getSIMList" v-model.trim="queryParams.dtuCode" />
        </template>
        <template slot-scope="scope">
          <text-highlight :queries="highlightInfo.dtuCode">{{ scope.row.dtuCode }}</text-highlight>
        </template>
      </el-table-column>
      <el-table-column width="110">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.deviceType') }}</div>
          <a-select style="width: 100%;" @change="getSIMList" v-model="filterParams.deviceType">
            <a-select-option value="">{{ $t('common.all') }}</a-select-option>
            <a-select-option
              v-for="(v, k) in deviceTypeMap"
              :key="k"
            >
              {{ v }}
            </a-select-option>
          </a-select>
        </template>
        <template slot-scope="scope">
          {{ deviceTypeMap[scope.row.deviceType] }}
        </template>
      </el-table-column>
      <el-table-column width="100">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.deviceStatus') }}</div>
          <a-select style="width: 100%;" @change="getSIMList" v-model="filterParams.deviceStatus">
            <a-select-option value="">{{ $t('common.all') }}</a-select-option>
            <a-select-option
              v-for="(v, k) in deviceStatusMap"
              :key="k"
            >
              {{ v }}
            </a-select-option>
          </a-select>
        </template>
        <template slot-scope="scope">
          <span :style="{ color: (scope.row.deviceStatus && deviceStatusMap[scope.row.deviceStatus]) ? runningStatus[scope.row.deviceStatus].color : '' }">{{ (scope.row.deviceStatus && runningStatus[scope.row.deviceStatus]) ? runningStatus[scope.row.deviceStatus][lang] : '' }}</span>
        </template>
      </el-table-column>
      <el-table-column width="200">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.company') }}<span class="icons unset" style="cursor: pointer;" data-index="company" @click="handleSortConditionChange($event, 'company')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-input allow-clear @keyup.enter="getSIMList" v-model.trim="queryParams.company" />
        </template>
        <template slot-scope="scope">
          <text-highlight :queries="highlightInfo.company">{{ scope.row.company }}</text-highlight>
        </template>
      </el-table-column>
      <el-table-column width="120">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.organization') }}<span class="icons unset" style="cursor: pointer;" data-index="organization" @click="handleSortConditionChange($event, 'organization')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-input allow-clear @keyup.enter="getSIMList" v-model.trim="queryParams.organization" />
        </template>
        <template slot-scope="scope">
          <text-highlight :queries="highlightInfo.organization">{{ scope.row.organization }}</text-highlight>
        </template>
      </el-table-column>
      <el-table-column width="220">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{  $t('SIM.createdAt') }}<span class="icons unset" style="cursor: pointer;" data-index="createdAt" @click="handleSortConditionChange($event, 'createdAt')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-range-picker v-model="createdAtTimeRange" @change="getSIMList('')" />
        </template>
        <template slot-scope="scope">
          {{ scope.row.createdAt }}
        </template>
      </el-table-column>
      <el-table-column width="220">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{  $t('SIM.powerOnTime') }}<span class="icons unset" style="cursor: pointer;" data-index="createdAt" @click="handleSortConditionChange($event, 'powerOnTime')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-range-picker v-model="powerOnTimeRange" @change="getSIMList('')" />
        </template>
        <template slot-scope="scope">
          {{ scope.row.powerOnTime }}
        </template>
      </el-table-column>
      <el-table-column width="240">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.currentBasicServicePackageName') }}<span class="icons unset" style="cursor: pointer;" data-index="currentBasicServicePackageName" @click="handleSortConditionChange($event, 'currentBasicServicePackageName')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-input allow-clear @keyup.enter="getSIMList" v-model.trim="queryParams.currentBasicServicePackageName" />
        </template>
        <template slot-scope="scope">
          <text-highlight :queries="highlightInfo.currentBasicServicePackageName">{{ scope.row.currentBasicServicePackageName }}</text-highlight>
        </template>
      </el-table-column>
      <el-table-column width="130">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.currentCycleDataUsage') }}<span class="icons unset" style="cursor: pointer;" data-index="currentCycleDataUsage" @click="handleSortConditionChange($event, 'currentCycleDataUsage')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-input allow-clear @keyup.enter="getSIMList" v-model.trim="queryParams.currentCycleDataUsage" />
        </template>
        <template slot-scope="scope">
          <text-highlight :queries="highlightInfo.currentCycleDataUsage">{{ scope.row.currentCycleDataUsage }}</text-highlight>
        </template>
      </el-table-column>
      <el-table-column width="220">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.activatedTime') }}<span class="icons unset" style="cursor: pointer;" data-index="activatedTime" @click="handleSortConditionChange($event, 'activatedTime')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-range-picker v-model="activatedTimeRange" @change="getSIMList('')" />
        </template>
        <template slot-scope="scope">
          {{ scope.row.activatedTime }}
        </template>
      </el-table-column>
      <el-table-column width="220">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('SIM.sellTime') }}<span class="icons unset" style="cursor: pointer;" data-index="sellTime" @click="handleSortConditionChange($event, 'sellTime')">
            <a-icon type="caret-up" />
            <a-icon type="caret-down" />
          </span></div>
          <a-range-picker v-model="sellTimeRange" @change="getSIMList('')" />
        </template>
        <template slot-scope="scope">
          {{ scope.row.sellTime }}
        </template>
      </el-table-column>
      <el-table-column width="210" fixed="right">
        <template #header>
          <div style="color: rgba(0, 0, 0, 0.85); font-weight: 500; text-align: left;">{{ $t('common.operation') }}</div>
        </template>
        <template slot-scope="scope">
          <a-popconfirm
            :title="$t('SIM.cta')"
            :ok-text="$t('common.yes')"
            :cancel-text="$t('common.no')"
            @confirm="activateSIM(scope.row)"
          >
            <a-button :loading="scope.row.isActivating" style="padding: 0; margin: 0" type="link">{{ $t('SIM.activate') }}</a-button>
          </a-popconfirm>
          <a-popconfirm
            :title="$t('SIM.ctd')"
            :ok-text="$t('common.yes')"
            :cancel-text="$t('common.no')"
            @confirm="deactivateSIM(scope.row)"
          >
            <a-button :loading="scope.row.isDectivating" style="padding: 0; margin: 0 10px" type="link">{{ $t('SIM.deactivate') }}</a-button>
          </a-popconfirm>
          <a-popconfirm
            :title="$t('SIM.ctr')"
            :ok-text="$t('common.yes')"
            :cancel-text="$t('common.no')"
            @confirm="retireSIM(scope.row)"
          >
            <a-button :loading="scope.row.isRetiring" style="padding: 0; margin: 0" type="link">{{ $t('SIM.retire') }}</a-button>
          </a-popconfirm>
          <a-popconfirm
            :title="$t('SIM.cts')"
            :ok-text="$t('common.yes')"
            :cancel-text="$t('common.no')"
            @confirm="sync(scope.row)"
          >
            <a-button :loading="scope.row.isSyncing" style="padding: 0; margin: 0; margin-left: 10px" type="link">{{ $t('SIM.sync_dtu') }}</a-button>
          </a-popconfirm>
        </template>
      </el-table-column>
    </el-table>
    <div>
      <a-pagination style="float: right; margin: 20px 0; padding-bottom: 20px" :hideOnSinglePage="true" :current="current" :pageSize="pageSize" :total="total" @change="handlePageChange" />
    </div>
  </div>
</template>

<script>
import { getDict } from '@/api/dict'
import {
  getSIMs,
  activateSIM,
  deactivateSIM,
  retireSIM,
  exportSIM,
  syncDtu
} from '@/api/device'
import en from 'element-ui/lib/locale/lang/en'
import zh from 'element-ui/lib/locale/lang/zh-CN'
import locale from 'element-ui/lib/locale'
import TextHighlight from "vue-text-highlight"
import moment from 'moment'

export default {
  components: {
    TextHighlight
  },
  data() {
    return {
      current: 1,
      pageSize: 10,
      total: 0,
      zh,
      en,
      isTableLoading: false,
      queryParams: {
        giccid: "",
        currentBasicServicePackageName: "",
        currentCycleDataUsage: "",
        deviceCode: "",
        deviceName: "",
        model: "",
        company: "",
        organization: "",
        dtuCode: ""
      },
      highlightInfo: {
        giccid: "",
        currentBasicServicePackageName: "",
        currentCycleDataUsage: "",
        deviceCode: "",
        deviceName: "",
        model: "",
        company: "",
        organization: "",
        dtuCode: ""
      },
      filterParams: {
        status: "",
        deviceType: "",
        deviceStatus: ""
      },
      sortParams: {},
      simTypeMap: {},
      deviceTypeMap: {},
      deviceStatusMap: {},
      SIMs: [],
      runningStatus: {
        online: {
          color: "#0eed24",
          zh: "在线",
          en: 'Online'
        },
        running: {
          color: "#0eed24",
          zh: "运行",
          en: 'Running'
        },
        maintain: {
          color: "#ff681c",
          zh: "保养",
          en: 'Maintain'
        },
        error: {
          color: "#e82127",
          zh: "故障",
          en: 'Error'
        },
        standby: {
          color: "#11d7f5",
          zh: "停机",
          en: 'Standby'
        },
        sleep: {
          color: "#11d7f5",
          zh: "待机",
          en: 'Sleep'
        },
        offline: {
          color: "#4d4d4d",
          zh: "离线",
          en: 'Offline'
        },
        prepare: {
          color: "#4d4d4d",
          zh: "备机",
          en: 'Prepare'
        }
      },
      createdAtTimeRange: [],
      powerOnTimeRange: [],
      activatedTimeRange: [],
      sellTimeRange: [],
      isExporting: false
    }
  },
  computed: {
    lang() {
      return this.$store.state.lang
    }
  },
  watch: {
    lang: {
      immediate: true,
      handler() {
        this.translate()
      }
    }
  },
  mounted() {
    this.render()
  },
  methods: {
    translate() {
      locale.use(this[this.lang])
      this.getSmarthingSIMStatus()
      this.getDeviceType()
      this.getDeviceStatusMap()
    },
    async render() {
      try {
        this.isTableLoading = true
        await this.getSmarthingSIMStatus()
        await this.getDeviceType()
        await this.getDeviceStatusMap()
        this.getSIMList()
      } catch(e) {
        console.log(e)
      }
    },
    async getSmarthingSIMStatus() {
      const simTypeMap = {}
      const { data } = await getDict(this.lang === 'en' ? 'en-US' : 'zh-CN', "smarthingSIMStatus")
      data.content.forEach(c => {
        simTypeMap[c.value] = c.text
      })
      this.simTypeMap = simTypeMap
    },
    async getDeviceType() {
      const deviceTypeMap = {}
      const { data } = await getDict(this.lang === 'en' ? 'en-US' : 'zh-CN', "deviceType")
      data.content.forEach(c => {
        deviceTypeMap[c.value] = c.text
      })
      this.deviceTypeMap = deviceTypeMap
    },
    async getDeviceStatusMap() {
      const deviceStatusMap = {}
      const { data } = await getDict(this.lang === 'en' ? 'en-US' : 'zh-CN', "deviceStatus")
      data.content.forEach(c => {
        deviceStatusMap[c.value] = c.text
      })
      this.deviceStatusMap = deviceStatusMap
    },
    handleSortConditionChange(e, field) {
      let node = e.target
      while (node.nodeName !== "SPAN") {
        node = node.parentNode
      }
      let c = node.classList[1]
      switch(c) {
        case "ASC":
          c = "DESC"
          break
        case "DESC":
          c = "unset"
          break
        case "unset":
          c = "ASC"
          break
        default:
          break
      }
      node.classList.remove(node.classList[1])
      node.classList.add(c)
      if (c === "unset") {
        delete this.sortParams[field]
      } else {
        console.log(c)
        this.sortParams[field] = c
      }
      this.getSIMList()
    },
    setSearchParams() {
      const params = {
        query: [],
        filter: [],
        sort: []
      }
      for (let q in this.queryParams) {
        this.queryParams[q] = this.queryParams[q].trim()
        if (this.queryParams[q]) {
          if (q === "deviceCode") {
            params.query.push({
              field: q,
              value: this.queryParams[q].split("-").join("")
            })
          } else {
            params.query.push({
              field: q,
              value: this.queryParams[q]
            })
          }
        }
      }
      for (let q in this.filterParams) {
        if (this.filterParams[q]) {
          params.filter.push({
            field: q,
            value: this.filterParams[q]
          })
        }
      }
      if (this.createdAtTimeRange.length) {
        params.filter.push({
          field: "createdAt",
          req: "between",
          value: [
            +new Date(this.createdAtTimeRange[0]._d).setHours(0, 0, 0, 0),
            +new Date(this.createdAtTimeRange[1]._d).setHours(23, 59, 59, 999)
          ]
        })
      }
      if (this.powerOnTimeRange.length) {
        params.filter.push({
          field: "powerOnTime",
          req: "between",
          value: [
            this.powerOnTimeRange[0].startOf("day").format("YYYY-MM-DD HH:mm:ss"),
            this.powerOnTimeRange[1].endOf("day").format("YYYY-MM-DD HH:mm:ss")
          ]
        })
      }
      if (this.activatedTimeRange.length) {
        params.filter.push({
          field: "activatedTime",
          req: "between",
          value: [
            this.activatedTimeRange[0].startOf("day").format("YYYY-MM-DD HH:mm:ss"),
            this.activatedTimeRange[1].endOf("day").format("YYYY-MM-DD HH:mm:ss")
          ]
        })
      }
      if (this.sellTimeRange.length) {
        params.filter.push({
          field: "sellTime",
          req: "between",
          value: [
            this.sellTimeRange[0].startOf("day").format("YYYY-MM-DD HH:mm:ss"),
            this.sellTimeRange[1].endOf("day").format("YYYY-MM-DD HH:mm:ss")
          ]
        })
      }
      params.filter.length && params.filter.push({
        field: "mode",
        value: "and"
      })
      for (let q in this.sortParams) {
        params.sort.push({
          field: q,
          value: this.sortParams[q]
        })
      }
      return params
    },
    async getSIMList(e) {
      this.isTableLoading = true
      if (e || e === "") {
        this.current = 1
      }
      const params = this.setSearchParams()
      try {
        const { data } = await getSIMs(this.current, this.pageSize, params)
        for (let q in this.highlightInfo) {
          this.highlightInfo[q] = this.queryParams[q]
        }
        this.current = data.current
        this.pageSize = data.size
        this.total = data.total
        this.SIMs = data.records.map(r => {
          r.createdAt = r.createdAt ? moment(r.createdAt).format("YYYY-MM-DD HH:mm:ss") : ""
          return r
        })
      } catch(e) {
        console.log(e)
      } finally {
        this.isTableLoading = false
      }
    },
    handlePageChange(p) {
      this.current = p
      this.getSIMList()
    },
    async activateSIM(row) {
      row.isActivating = true
      this.SIMs = [...this.SIMs]
      try {
        const { message } = await activateSIM(row.giccid)
        this.$message.success(message)
      } catch(e) {
        console.log(e)
      } finally {
        row.isActivating = false
        this.SIMs = [...this.SIMs]
      }
    },
    async deactivateSIM(row) {
      row.isDectivating = true
      this.SIMs = [...this.SIMs]
      try {
        const { message } = await deactivateSIM(row.giccid)
        this.$message.success(message)
      } catch(e) {
        console.log(e)
      } finally {
        row.isDectivating = false
        this.SIMs = [...this.SIMs]
      }
    },
    async retireSIM(row) {
      row.isRetiring = true
      this.SIMs = [...this.SIMs]
      try {
        const { message } = await retireSIM(row.giccid)
        this.$message.success(message)
      } catch(e) {
        console.log(e)
      } finally {
        row.isRetiring = false
        this.SIMs = [...this.SIMs]
      }
    },
    async handleExport() {
      this.isExporting = true
      try {
        const result = await exportSIM(this.setSearchParams())
        if (result) {
          const url = window.URL.createObjectURL(
            new Blob([result.data], { type: "application/xlsx" })
          );
          const b = document.createElement("a")
          b.download = "SIM.xlsx"
          b.href = url
          b.click()
          URL.revokeObjectURL(url)
        }
      } catch(e) {
        console.log(e)
      } finally {
        this.isExporting = false
      }
    },
    async sync(row) {
      row.isSyncing = true
      this.SIMs = [...this.SIMs]
      try {
        const { message } = await syncDtu(row.dtuCode)
        this.$message.success(message)
      } catch(e) {
        console.log(e)
      } finally {
        row.isSyncing = false
        this.SIMs = [...this.SIMs]
      }
    }
  }
}
</script>

<style lang="less" scoped>
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
span {
  position: relative;
}
.anticon-caret-up {
  position: absolute;
  top: -1px;
  right: -18px;
  font-size: 14px;
  color: #ccc;
}
span.ASC .anticon-caret-up {
  color: #1890ff;
}
span.icons {
  position: absolute;
  right: 27px;
}
span.DESC .anticon-caret-down {
  color: #1890ff;
}
.anticon-caret-down {
  position: absolute;
  top: 8px;
  right: -18px;
  font-size: 14px;
  color: #ccc;
}
/deep/ .ant-select-selection__rendered {
  font-weight: normal;
}
</style>